import React, { useRef, useState, useEffect } from 'react';
import { Printer, Calendar, User, MapPin, Upload, CheckCircle, Save, Edit } from 'lucide-react';
import apiService from "../../config";

const Akshep = ({ setSubComponent, onBack }) => {
    const documentRef = useRef(null);
    const [objectionList, setObjectionList] = useState([]);
    const [headingInfo, setHeadingInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [projectId, setProjectId] = useState(null);
    const [proposalId, setProposalId] = useState(null);
    const [editRowId, setEditRowId] = useState(null);
    const [editedResolution, setEditedResolution] = useState("");
    const [showDocModal, setShowDocModal] = useState(false);
    const [previewDocUrl, setPreviewDocUrl] = useState("");
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedObjection, setSelectedObjection] = useState(null);
    const [resolutionText, setResolutionText] = useState("");
    const [saving, setSaving] = useState(false);
    const [objectionStatus, setObjectionStatus] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSavingHeader, setIsSavingHeader] = useState(false);
    const DEFAULT_HEADER = `जिगांव प्रकल्प तालुका नांद्रा जिल्हा बुलढाणा

रजिगांव प्रकल्पग्रस्तांकडे ठो ठलेन नॉवे आडोळ ट.तालुका जळगांव जामोद जिल्हा बुलढाणा येथील प्रकल्पग्रस्तांचे पुनर्वसना अन्वये भूखंड वाटपारुचे नमुनागदन था कापयुनसुंगाचे धर का.क्र.पुनर्वसि/ब-२१/बिसि/कोळी/१५५/२०२२ दिनांक २५/०६/२०२२ अन्वये भूखंड वाटपाची याट काही समितिस्त केली नोदातं राहयापुत्र प्राप्त झालेले आठनेपाचे किरिजरच फरसत स्युवाहिकसठन अनुप्रामाना अहवाल.`;

   const [headerContent, setHeaderContent] = useState(DEFAULT_HEADER);



    const [locationInfo, setLocationInfo] = useState({
        district: '',
        taluka: '',
        village: ''
    })
    const [formData, setFormData] = useState({
        objectionDate: "",
        objectorName: "",
        objectorAddress: "",
        gateNo: "",
        ownerName: "",
        description: "",
        objection_resolution: ""
    });

    useEffect(() => {
        if (projectId && proposalId) {
            fetchLandObjections(projectId, proposalId);
        }
    }, [projectId, proposalId]);


    // Get projectId & proposalId (same logic as Kalam11)
    useEffect(() => {
        const getProjectId = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                const p = JSON.parse(currentProjectData);
                return p.project_id || p.id;
            }

            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                const p = JSON.parse(selectedProject);
                return p.project_id || p.id;
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                const u = JSON.parse(userData);
                return u?.data?.project_id || u?.project_id || null;
            }
            return null;
        };

        const getProposalId = () => {
            const userData = JSON.parse(localStorage.getItem('userData') || '{}');
            return (
                userData?.data?.default_proposal ||
                userData?.data?.proposal_id ||
                userData?.default_proposal ||
                userData?.proposal_id ||
                null
            );
        };

        setProjectId(getProjectId());
        setProposalId(getProposalId());
    }, []);


    const fetchLandObjections = async (projectId, proposalId) => {
        try {
            setLoading(true);
            const res = await apiService.getLandObjectionById(projectId, proposalId);

            if (res?.status === 200 && Array.isArray(res.data)) {

                // ✅ SORT BY objection_id ASC (old → new)
                const sortedData = [...res.data].sort(
                    (a, b) => a.objection_id - b.objection_id
                );

                setObjectionList(sortedData);

                const transformedData = sortedData.map((item, index) => ({
                    objection_id: item.objection_id,   // ✅ KEEP ID
                    date: formatDate(item.objection_date),
                    objector: item.objector_name || "",
                    objection: item.objection_details || "",
                    clarification: item.objection_resolution || "",
                    document: item.document || null,
                    objection_status: item.objection_status || "",
                    objection_completion_date: (formatForInputDate(item.objection_completion_date))
                }));

                setTableData(transformedData);
            }
        } catch (e) {
            console.error("Error fetching objections:", e);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
  if (!proposalId) return;

  const fetchProposalHeader = async () => {
    try {
      const response = await apiService.get(
        `/getProposal?proposalId=${proposalId}`
      );

        if (response?.status === 200) {
        const backendHeader = response?.data?.land_objection_header;

        if (backendHeader && backendHeader.trim() !== "") {
          setHeaderContent(backendHeader);
        } else {
          setHeaderContent(DEFAULT_HEADER);
        }
      }
    } catch (error) {
      console.error("Failed to fetch proposal header:", error);
    }
  };

  fetchProposalHeader();
}, [proposalId]);


    const handleHeaderSave = async () => {
  try {
    setIsSavingHeader(true); 
    const formDataToSend = new FormData();
    formDataToSend.append("land_objection_header", headerContent);

    const response = await apiService.patch(
      `/patchUpdateProposal?proposalId=${proposalId}`,
      formDataToSend
    );

    if (response?.status === 200) {
      setIsEditing(false); // ✅ exit edit mode
      alert("Header updated successfully");
    }
  } catch (error) {
    console.error("Header update failed:", error);
    alert("Failed to update header");
  }finally {
    setIsSavingHeader(false); 
  }
};


    const formatForInputDate = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.split("T")[0];
};


    const [tableData, setTableData] = useState([]);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [owners, setOwners] = useState([]);
    const [selectedOwnerId, setSelectedOwnerId] = useState("");
    const [ownerMessage, setOwnerMessage] = useState("");
    const [showForm, setShowForm] = useState(true);

    const language = 'marathi'; // You can make this dynamic
    const translations = {
        marathi: {
            objectionDetails: "आक्षेप तपशील",
            field: "फील्ड",
            value: "मूल्य",
            objectionDate: "आक्षेप दिनांक",
            objectorName: "आक्षेपकर्त्याचे नाव",
            objectorAddress: "आक्षेपकर्त्याचा पत्ता",
            SurveyNo: "सर्व्हे क्र.",
            GatNo: "गट क्र.",
            PlotNo: "प्लॉट क्र.",
            GharNo: "घर क्र.",
            SelectOwner: "मालक निवडा",
            uploadDocumentss: "दस्तऐवज अपलोड करा",
            RaiseObjection: "आक्षेप नोंदवा"
        }
    };

    // New: Fetch owners when Gat No entered
    useEffect(() => {
        const fetchOwners = async () => {
            if (formData.gateNo && projectId && proposalId) {
                try {
                    const res = await apiService.getVerifyOwnerSurveyNo(
                        formData.gateNo,
                        projectId,
                        proposalId
                    );

                    console.log("Full API Response:", res);

                    if (res?.status === 200 && res?.verify && Array.isArray(res.data)) {
                        setOwners(res.data);
                        setOwnerMessage(""); // ✅ clear old messages
                    } else if (res?.status === 200 && !res?.verify && res?.message) {
                        setOwners([]);
                        setOwnerMessage("No owners found for this survey number.");
                    } else {
                        setOwners([]);
                        setOwnerMessage("No owner data available.");
                    }
                } catch (err) {
                    console.error("Error fetching owners:", err);
                    setOwners([]);
                    setOwnerMessage("Failed to load owner data.");
                }
            }
        };

        fetchOwners();
    }, [formData.gateNo, projectId, proposalId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleOwnerSelect = (e) => {
        setSelectedOwnerId(e.target.value);
        const selectedOwner = owners.find(o => o.land_owner_id === e.target.value);
        if (selectedOwner) {
            setFormData(prev => ({
                ...prev,
                ownerName: selectedOwner.owner_name
            }));
        }
    };

    const handleSave = async () => {
        // Basic validation
        if (!formData.objectionDate || !formData.objectorName || !formData.objectorAddress) {
            alert("कृपया आवश्यक फील्ड भरा (Please fill required fields)");
            return;
        }

        try {
            setIsLoading(true);

            // Build FormData as backend expects
            const payload = new FormData();

            payload.append("project_id", projectId);
            payload.append("proposal_id", proposalId);
            payload.append("house_no", formData.gateNo || "");
            payload.append("objector_name", formData.objectorName);
            payload.append("owner_id", selectedOwnerId || "");
            payload.append("objection_date", formData.objectionDate);
            payload.append(
                "objection_details", formData.description
            );
            payload.append(
                "objection_resolution",
                formData.objection_resolution
            );

            // File (optional)
            if (selectedFile) {
                payload.append("document", selectedFile);
            }

            // API CALL
            const response = await apiService.postAddObjection(payload);

            console.log("✅ API Success:", response);

            // Refresh the objection list after successful submission
            fetchLandObjections(projectId, proposalId);
            setShowForm(false);

            // Reset form
            setFormData({
                objectionDate: "",
                objectorName: "",
                objectorAddress: "",
                gateNo: "",
                ownerName: "",
                description: "",
                objectionDate: ""
            });

            setSelectedFile(null);
            setSelectedOwnerId("");
            setShowForm(false);

            alert("✅ आक्षेप यशस्वीरित्या नोंदवला गेला!");

        } catch (error) {
            console.error("❌ Objection submit failed:", error);
            alert(
                error.message ||
                "आक्षेप नोंदवताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा."
            );
        } finally {
            setIsLoading(false);
        }
    };


    const handlePrint = () => {
        const printContent = documentRef.current?.innerHTML;
        if (!printContent) {
            alert("No content available to print.");
            return;
        }

        const win = window.open("", "", "height=900,width=1200");
        win.document.write(`
      <html>
        <head>
          <title>जिगांव प्रकल्प आक्षेप व स्पष्टीकरण</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 0.4in 0.3in;
            }
            
            body {
              font-family: 'Noto Sans Devanagari', 'Mangal', 'Arial', sans-serif;
              font-size: 15px;
              line-height: 1.4;
              color: #000;
              margin: 0;
              padding: 0;
            }

            .text-center {
              text-align: center;
            }

            .font-bold {
              font-weight: bold;
            }

            .mb-2 {
              margin-bottom: 4px;
            }

            .mb-3 {
              margin-bottom: 6px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-top: 20px;
            }

            th, td {
              border: 1px solid #000;
              padding: 5px 8px;
              vertical-align: top;
            }

            th {
              font-weight: bold;
              text-align: center;
            }

            .noprint {
            display : none;
            }

            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

        win.document.close();
        win.print();
    };

    const handleSaveResolution = async () => {
  if (!resolutionText.trim()) {
    alert("Please enter resolution");
    return;
  }

  try {
    setSaving(true);

    const payload = {
      objector_name: selectedObjection.objector,
      objection_date: selectedObjection.date,
      objection_details: selectedObjection.objection,
      objection_resolution: resolutionText,
      objection_status: objectionStatus,
      objection_completion_date: completionDate
    };

    await apiService.patchUpdateLandPlotObjection(
      selectedObjection.objection_id,
      payload
    );

    setShowDetailsModal(false);
    setSelectedObjection(null);
    setResolutionText("");

    fetchLandObjections(projectId, proposalId); // refresh list

  } catch (err) {
    console.error("❌ Update error", err);
    alert("Failed to update objection");
  } finally {
    setSaving(false);
  }
};


    const handleViewDocument = (docUrl) => {
        if (!docUrl) return;
        setPreviewDocUrl(docUrl);
        setShowDocModal(true);
    };

    const openDetailsModal = (row) => {
    setSelectedObjection(row);
    setResolutionText(row.clarification || "");
    setObjectionStatus(row.objection_status || "");
    setCompletionDate(row.objection_completion_date || "");
    setShowDetailsModal(true);
    };


    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Form Section */}
            {showForm && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
                        <h2 className="text-xl font-semibold text-white">{translations[language].objectionDetails}</h2>
                    </div>

                    <div className="p-6">
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="text-left py-3 px-4 font-semibold text-gray-700 border border-gray-200 w-1/4">
                                            {translations[language].field}
                                        </th>
                                        <th className="text-left py-3 px-4 font-semibold text-gray-700 border border-gray-200">
                                            {translations[language].value}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Objection Date */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="h-4 w-4 text-blue-600" />
                                                <span>{translations[language].objectionDate} *</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <input
                                                type="date"
                                                name="objectionDate"
                                                value={formData.objectionDate}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            />
                                        </td>
                                    </tr>

                                    {/* Objector Name */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <User className="h-4 w-4 text-blue-600" />
                                                <span>{translations[language].objectorName} *</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <input
                                                type="text"
                                                name="objectorName"
                                                value={formData.objectorName}
                                                onChange={handleInputChange}
                                                placeholder="Enter objector's full name"
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            />
                                        </td>
                                    </tr>

                                    {/* Objector Address */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <MapPin className="h-4 w-4 text-blue-600" />
                                                <span>{translations[language].objectorAddress} *</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <textarea
                                                name="objectorAddress"
                                                value={formData.objectorAddress}
                                                onChange={handleInputChange}
                                                placeholder="Enter complete address"
                                                required
                                                rows="3"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                                            />
                                        </td>
                                    </tr>

                                    {/* Gate No */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <span>{translations[language].SurveyNo}/ {translations[language].GatNo}/ {translations[language].PlotNo}/ {translations[language].GharNo}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <input
                                                type="text"
                                                name="gateNo"
                                                value={formData.gateNo}
                                                onChange={handleInputChange}
                                                placeholder="Enter gate number (optional)"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            />
                                        </td>
                                    </tr>

                                    {/* Owner Dropdown */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <User className="h-4 w-4 text-blue-600" />
                                                <span>{translations[language].SelectOwner}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            {owners.length > 0 ? (
                                                <select
                                                    value={selectedOwnerId}
                                                    onChange={handleOwnerSelect}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                >
                                                    <option value="">-- Select Owner --</option>
                                                    {owners.map((owner) => (
                                                        <option key={owner.land_owner_id} value={owner.land_owner_id}>
                                                            {owner.owner_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : ownerMessage ? (
                                                <div className="text-red-600 text-sm font-medium">{ownerMessage}</div>
                                            ) : (
                                                <div className="text-gray-400 text-sm">Enter Gat No to load owners</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Description */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span>{translations[language].objectionDetails}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Enter objection description (optional)"
                                                rows="4"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                                            />
                                        </td>
                                    </tr>

                                    {/* File Upload */}
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-4 border border-gray-200 font-medium text-gray-700">
                                            <div className="flex items-center space-x-2">
                                                <Upload className="h-4 w-4 text-blue-600" />
                                                <span>{translations[language].uploadDocumentss}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 border border-gray-200">
                                            <div className="space-y-2">
                                                <input
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                />
                                                {selectedFile && (
                                                    <div className="flex items-center space-x-2 text-sm text-green-700 bg-green-50 p-2 rounded-lg">
                                                        <CheckCircle className="h-4 w-4" />
                                                        <span>Selected: {selectedFile.name}</span>
                                                        <button
                                                            onClick={() => {
                                                                const fileURL = URL.createObjectURL(selectedFile);
                                                                window.open(fileURL, "_blank");
                                                            }}
                                                            className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 mt-8 pt-6">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            )}

                            <button
                                onClick={handleSave}
                                disabled={isLoading}
                                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 transition-all duration-300 shadow-lg shadow-blue-200 hover:shadow-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-5 w-5" />
                                        <span>{translations[language].RaiseObjection}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Show Form Button (when form is hidden) */}
            {!showForm && (
                <div className="flex justify-end">
                    <button
                        onClick={() => setShowForm(true)}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
                    >
                        + Add New Objection
                    </button>
                </div>
            )}

            {/* Print Button */}
            <div className="flex justify-start">
                <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg"
                >
                    <Printer size={20} />
                    Print
                </button>
            </div>

            {/* Document Preview */}
            <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
                <div
                    ref={documentRef}
                    className="document-container"
                    style={{
                        fontFamily: "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif",
                        padding: "25px",
                        fontSize: "15px",
                        lineHeight: "1.5"
                    }}
                >
                    {/* Header */}
                    <div className="flex justify-end mb-2 noprint">
                        <button
                            disabled={isSavingHeader}
                            onClick={() => {
                            if (isEditing) {
                                handleHeaderSave(); // SAVE
                            } else {
                                setIsEditing(true); // EDIT
                            }
                            }}
                            className={`flex items-center gap-2 px-3 py-1 text-white rounded transition
                            ${
                                isEditing
                                ? "bg-green-600 hover:bg-green-700"
                                : "bg-blue-600 hover:bg-blue-700"
                            }
                            ${isSavingHeader ? "opacity-60 cursor-not-allowed" : ""}
                            `}
                        >
                            {isSavingHeader ? (
                            <>
                                <svg
                                className="animate-spin h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                />
                                </svg>
                            </>
                            ) : (
                            <>
                                {isEditing ? <Save size={16} /> : <Edit size={16} />}
                            </>
                            )}
                        </button>
                        </div>


                    <div className="mb-4">
                    {isEditing ? (
                        <textarea
                        value={headerContent}
                        onChange={(e) => setHeaderContent(e.target.value)}
                        rows={6}
                        className="w-full border border-gray-400 rounded p-3"
                        />
                    ) : (
                        <>
                        <div className="text-center font-bold mb-3">
                            {headerContent.split("\n")[0]}
                        </div>
                        <div className="text-justify">
                            {headerContent.split("\n").slice(1).join("\n")}
                        </div>
                        </>
                    )}
                    </div>

                    {/* Table */}
                    <table className="w-full border-2 border-black">
                        <thead>
                            <tr className="font-bold">
                            <th className="border border-black p-2 text-center noprint">Action</th>
                            <th className="border border-black p-2 text-center noprint">Status</th>

                                <th className="border border-black p-2">अ.क्र.</th>
                                <th className="border border-black p-2">आक्षेप प्राप्त<br />दिनांक</th>
                                <th className="border border-black p-2">आक्षेपकर्त्याचे नाव</th>
                                <th className="border border-black p-2">आक्षेप</th>
                                <th className="border border-black p-2">आक्षेपाचे निराकरण</th>
                                {/* <th className="border border-black p-2 noprint">Document</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="border border-black p-4 text-center">
                                        Loading objections...
                                    </td>
                                </tr>
                            ) : tableData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="border border-black p-4 text-center">
                                        No objections found
                                    </td>
                                </tr>
                            ) : (
                                tableData.map((row, idx) => (
                                    <tr key={row.objection_id}>
                                    <td className="border border-black p-2 text-center noprint">
                                    <button
                                        className="bg-blue-600 text-white px-3 py-1 rounded"
                                        onClick={() => openDetailsModal(row)}
                                    >
                                         <Edit className="h-4 w-4" />
                                    </button>
                                    </td>

                                    <td className="border border-black p-2 text-center noprint">
                                    <span
                                        className={`px-2 py-1 rounded text-xs font-semibold
                                        ${row.objection_status === "Resolved" && "bg-green-100 text-green-700"}
                                        ${row.objection_status === "Pending" && "bg-yellow-100 text-yellow-700"}
                                        ${row.objection_status === "Rejected" && "bg-red-100 text-red-700"}
                                        `}
                                    >
                                        {row.objection_status || "Pending"}
                                    </span>
                                    </td>


                                        <td className="border border-black p-2 text-center">
                                            {idx + 1}
                                        </td>

                                        <td className="border border-black p-2 text-center">
                                            {row.date}
                                        </td>

                                        <td className="border border-black p-2">
                                            {row.objector}
                                        </td>

                                        <td className="border border-black p-2">
                                            {row.objection}
                                        </td>

                                        <td className="border border-black p-2">
                                            {row.clarification}
                                        </td>

                                        {/* ✅ DOCUMENT COLUMN */}
                                        {/* <td className="border border-black p-2 text-center noprint">
                                            {row.document ? (
                                                <button
                                                    className="bg-indigo-600 text-white px-3 py-1 rounded"
                                                    onClick={() => handleViewDocument(row.document)}
                                                >
                                                    View
                                                </button>
                                            ) : (
                                                <span className="text-gray-400">N/A</span>
                                            )}
                                        </td> */}
                                    </tr>
                                ))
                            )}
                        </tbody>

                    </table>
                </div>
            </div>
            {showDocModal && (
<div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg w-[90%] max-w-4xl p-4 relative">

                        <button
                            className="absolute top-2 right-3 text-xl font-bold"
                            onClick={() => setShowDocModal(false)}
                        >
                            ✕
                        </button>

                        <h2 className="text-lg font-semibold mb-3">
                            Document Preview
                        </h2>

                        {/* IMAGE PREVIEW */}
                        {previewDocUrl.match(/\.(jpg|jpeg|png)$/i) && (
                            <img
                                src={previewDocUrl}
                                alt="Document"
                                className="max-h-[70vh] mx-auto"
                            />
                        )}

                        {/* PDF PREVIEW */}
                        {previewDocUrl.match(/\.pdf$/i) && (
                            <iframe
                                src={previewDocUrl}
                                title="PDF Preview"
                                className="w-full h-[70vh] border"
                            />
                        )}

                    </div>
                </div>
            )}

          {showDetailsModal && selectedObjection && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      
      {/* Header with gradient accent */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Objection Details
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Review and resolve the objection
            </p>
          </div>
          <button
            onClick={() => setShowDetailsModal(false)}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 space-y-5">
        
        {/* Info Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Objector
            </div>
            <div className="text-base font-medium text-gray-900">
              {selectedObjection.objector}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Objection Date
            </div>
            <div className="text-base font-medium text-gray-900">
              {selectedObjection.date}
            </div>
          </div>
        </div>

        {/* Objection Content */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-semibold text-gray-900">
              Objection Details
            </label>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <p className="text-sm text-gray-700 leading-relaxed">
              {selectedObjection.objection}
            </p>
          </div>
        </div>

        {selectedObjection?.document && (
  <div>
    <label className="text-sm font-semibold text-gray-900 mb-2 block">
      Attached Document
    </label>

    <button
      onClick={() => handleViewDocument(selectedObjection.document)}
      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828
             a4 4 0 00-5.656-5.656L6.343 10.343
             a6 6 0 108.485 8.485L20.5 13"
        />
      </svg>
      View Document
    </button>
  </div>
)}

        
        <div className="grid grid-cols-2 gap-4">
        {/* Objection Status */}
        <div>
        <label className="text-sm font-semibold text-gray-900 mb-1 block">
            Objection Status
        </label>
        <select
            value={objectionStatus}
            onChange={(e) => setObjectionStatus(e.target.value)}
            className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500"
        >
            <option value="">-- Select Status --</option>
            <option value="Pending">Pending</option>
            <option value="Resolved">Resolved</option>
            <option value="Rejected">Rejected</option>
        </select>
        </div>

        {/* Completion Date */}
        <div>
        <label className="text-sm font-semibold text-gray-900 mb-1 block">
            Completion Date
        </label>
        <input
            type="date"
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
            className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500"
        />
        </div>
        </div>

        {/* Resolution Input */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-semibold text-gray-900">
              Resolution
            </label>
          </div>
          <textarea
            value={resolutionText}
            onChange={(e) => setResolutionText(e.target.value)}
            rows={5}
            placeholder="Enter your resolution details here..."
            className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-shadow resize-none"
          />
        </div>
      </div>


      {/* Footer */}
      <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 rounded-b-2xl flex justify-end gap-3">
        <button
          className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          onClick={() => setShowDetailsModal(false)}
        >
          Cancel
        </button>

        <button
          className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          disabled={saving}
          onClick={handleSaveResolution}
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Resolution
            </>
          )}
        </button>
      </div>
    </div>
  </div>
)}


        </div>
    );
};

export default Akshep;